import {
  BookOpenIcon,
  BrainIcon,
  BriefcaseIcon,
  ClapperboardIcon,
  GraduationCapIcon,
  HeadphonesIcon,
  HeartIcon,
  LibraryIcon,
  MapPinIcon,
  MicIcon,
  PlaneIcon,
  PuzzleIcon,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { GoalId, QuizItem, ReminderId, SelfLevel, SkillKey } from "../types/onboarding";

export const MAX_GOALS = 3;
export const MAX_TARGETS = 3;

export const goalOptions: { id: GoalId; label: string; hint: string; icon: LucideIcon; boosts: SkillKey[] }[] = [
  { id: "travel", label: "Travel", hint: "Get around, order food, chat with locals", icon: PlaneIcon, boosts: ["speaking", "listening"] },
  { id: "work", label: "Work & career", hint: "Meetings, emails and colleagues", icon: BriefcaseIcon, boosts: ["vocab", "reading"] },
  { id: "family", label: "Family & friends", hint: "Connect with people you care about", icon: HeartIcon, boosts: ["speaking", "listening"] },
  { id: "study", label: "School & exams", hint: "Grammar that holds up in a test", icon: GraduationCapIcon, boosts: ["grammar", "reading"] },
  { id: "culture", label: "Films, music & books", hint: "Enjoy them in the original", icon: ClapperboardIcon, boosts: ["listening", "reading"] },
  { id: "move", label: "Moving abroad", hint: "Everyday life, paperwork, neighbours", icon: MapPinIcon, boosts: ["speaking", "vocab"] },
  { id: "brain", label: "Keep my brain sharp", hint: "A little bit, every day", icon: BrainIcon, boosts: ["vocab", "grammar"] },
];

export const levelOptions: { value: SelfLevel; label: string; hint: string }[] = [
  { value: 0, label: "I'm brand new", hint: "Start from the very first word" },
  { value: 1, label: "I know a few words", hint: "Hello, thank you, some numbers" },
  { value: 2, label: "I can get by", hint: "Simple chats about everyday things" },
  { value: 3, label: "I'm fairly confident", hint: "Most topics, with a few mistakes" },
];

/** Language-agnostic "can-do" statements, loosely following CEFR A1 → B1. */
export const canDoStatements = [
  "Introduce yourself and say where you're from",
  "Order food and ask for the bill",
  "Talk about what you did last weekend",
  "Follow a podcast or TV show without subtitles",
];

export const canDoAnswers = [
  { value: 0 as const, label: "Not yet" },
  { value: 1 as const, label: "With effort" },
  { value: 2 as const, label: "Easily" },
];

/** Short adaptive checks, only where we have content. Questions get harder; stops after two misses. */
export const placementQuiz: Record<string, QuizItem[]> = {
  es: [
    { id: "q1", prompt: "How do you say “thank you”?", options: ["Hola", "Gracias", "Perdón", "Adiós"], answer: 1 },
    { id: "q2", prompt: "Fill the gap", sentence: "Yo ___ estudiante.", options: ["estoy", "es", "soy", "son"], answer: 2 },
    {
      id: "q3",
      prompt: "What does this mean?",
      sentence: "Me gustaría un café, por favor.",
      options: ["I'd like a coffee, please.", "I really like coffee.", "I drank a coffee.", "Is there any coffee?"],
      answer: 0,
    },
    { id: "q4", prompt: "Fill the gap", sentence: "Ayer ___ al cine con mis amigos.", options: ["voy", "iré", "vaya", "fui"], answer: 3 },
    {
      id: "q5",
      prompt: "Fill the gap",
      sentence: "Si tuviera más tiempo, ___ por todo el mundo.",
      options: ["viajo", "viajaría", "viajé", "viajaré"],
      answer: 1,
    },
  ],
};

export const skillOptions: { key: SkillKey; label: string; hint: string; icon: LucideIcon }[] = [
  { key: "speaking", label: "Speaking", hint: "Say it out loud with confidence", icon: MicIcon },
  { key: "listening", label: "Listening", hint: "Understand real, fast speech", icon: HeadphonesIcon },
  { key: "vocab", label: "Vocabulary", hint: "More words, remembered longer", icon: LibraryIcon },
  { key: "grammar", label: "Grammar", hint: "Build sentences that feel right", icon: PuzzleIcon },
  { key: "reading", label: "Reading", hint: "Texts, signs, messages and books", icon: BookOpenIcon },
];

export const minuteOptions = [
  { minutes: 5, label: "Casual", xp: 20 },
  { minutes: 10, label: "Regular", xp: 40 },
  { minutes: 15, label: "Serious", xp: 60 },
  { minutes: 20, label: "Intense", xp: 80 },
];

export const reminderOptions: { id: ReminderId; label: string; time: string }[] = [
  { id: "morning", label: "Morning", time: "08:00" },
  { id: "lunch", label: "Lunch", time: "12:30" },
  { id: "evening", label: "Evening", time: "19:00" },
  { id: "none", label: "No reminders", time: "" },
];
